import { AxiosResponse } from 'axios';
import Breadcrumbs from 'components/breadcrumbs';
import Dialog from 'components/dialog';
import Error from 'components/error';
import FilterSidebar from 'components/filter-sidebar';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import Mui from 'materials';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { generatePath, useHistory, useRouteMatch } from 'react-router';
import { OwnerType } from 'types/shared/owner-type.enum';
import EditStocking from 'views/planning-prognosis/features/view-fish-group/components/edit-stocking';
import FishGroupGraph from 'views/planning-prognosis/features/view-fish-group/components/fish-group-graph';
import FishGroupPlanning from 'views/planning-prognosis/features/view-fish-group/components/fish-group-planning';
import FishGroupSnapshot from 'views/planning-prognosis/features/view-fish-group/components/fish-group-snapshot';
import FishGroupStocking from 'views/planning-prognosis/features/view-fish-group/components/fish-group-stocking';
import TemporaryPlanning from 'views/planning-prognosis/features/view-fish-group/components/temporary-planning';
import { FishGroupSnapshot as SnapshotModel } from 'views/planning-prognosis/features/view-fish-group/types/fish-group-snapshot.type';
import { FishGroup } from 'views/planning-prognosis/features/view-fish-group/types/fish-group.type';

import { Alert, Box, Button, CircularProgress, Grid } from '@mui/material';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import _ from 'underscore';
import { postCalculation } from 'views/planning-prognosis/api/create-calculation';
import { getPlanningPrognosisSite } from 'views/planning-prognosis/api/get-site';
import { getFishGroups } from 'views/planning-prognosis/api/get-site-fish-groups';
import { getHarvestedFishGroups } from 'views/planning-prognosis/features/view-fish-group/api/get-harvested-fish-groups';
import PlanningPrognosisTabsHeader from '../../components/planning-prognosis-tabs-header';
import { getDefaultFishGroupProfiles } from './api/fish-group-planning/get-default-fish-group-profiles';
import { getFishGroup, getInactiveFishGroup } from './api/get-fish-group';
import { getFishGroupCalculationStatus } from './api/get-fish-group-calculation-status';
import { deleteVirtualFishGroup } from './api/virtual-fish-group/delete-virtual-fish-group';

type ParamTypes = {
  localSiteId: string;
  fishGroupId: string;
  fishGroupTab: Tabs;
};

enum Tabs {
  FISH_GROUPS = 'fishgroups',
  VIRTUAL = 'virtual',
  HARVESTED = 'harvested',
}

type Props = {};

// technically, this is not a sub feature, but a part of the /sites/. Maybe all view-fish-group related components should belong to its own components folder in /sites/ feature?

const ViewFishGroup: React.FC<Props> = () => {
  const intl = useIntl();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { params, path } = useRouteMatch<ParamTypes>();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const removeDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const PAGE_NAME_ID = 'planningPrognosis.viewFishGroup';

  // #region STATE

  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<SnapshotModel>();
  const previousRecalculating = useRef<boolean | undefined>();

  // #endregion STATE

  // #region FETCH DATA

  const { data: site } = useGet(
    ['getPlanningPrognosisSite', account.company?.companyId, params.localSiteId],
    (context) => getPlanningPrognosisSite(String(context.queryKey[1]), String(context.queryKey[2])),
    {
      enabled: !!account.company?.companyId && !!params.localSiteId,
    }
  );

  const {
    data: fishGroup,
    error,
    isLoading,
    isError,
  } = useGet(
    ['getFishGroup', site?.globalSiteId, params.fishGroupId],
    (context) =>
      params.fishGroupTab === Tabs.HARVESTED
        ? getInactiveFishGroup(String(context.queryKey[1]), String(context.queryKey[2]))
        : getFishGroup(String(context.queryKey[1]), String(context.queryKey[2])),
    {
      enabled: !!site?.globalSiteId && !!params.fishGroupId,
    }
  );

  const { data: defaultProfile } = useGet(
    ['getDefaultFishGroupProfiles', params.fishGroupId],
    ({ queryKey }) => getDefaultFishGroupProfiles(String(queryKey[1])),
    {
      enabled: !!params.fishGroupId,
    }
  );

  const { data: fishGroups } = useGet(
    ['getFishGroups', fishGroup?.globalSiteId],
    (context) => getFishGroups(String(context.queryKey[1])),
    {
      enabled: !!fishGroup?.globalSiteId,
    }
  );

  const { data: harvested } = useGet(
    ['getHarvestedFishGroups', fishGroup?.globalSiteId],
    (context) => getHarvestedFishGroups(String(context.queryKey[1])),
    {
      enabled: !!fishGroup?.globalSiteId,
    }
  );

  const { data: calculationStatus } = useGet(
    ['getFishGroupCalculationStatus', fishGroup?.globalSiteId, fishGroup?.fishGroupId],
    (context) => getFishGroupCalculationStatus(String(context.queryKey[1]), String(context.queryKey[2])),
    {
      enabled: !!fishGroup?.globalSiteId && !!fishGroup?.fishGroupId,
      refetchInterval: refetchInterval,
    }
  );

  useEffect(() => {
    if (!calculationStatus) return;

    if (previousRecalculating.current === true && !calculationStatus.recalculating) {
      queryClient.invalidateQueries({
        queryKey: ['getFishGroup'],
      });
    }

    previousRecalculating.current = calculationStatus.recalculating;
    setRefetchInterval(calculationStatus.recalculating ? 10000 : false);
  }, [calculationStatus, queryClient]);

  // #endregion FETCH DATA

  // #region POST DATA

  const startCalculation = usePost<AxiosResponse, { owner: OwnerType; id: string }>((request) =>
    postCalculation(request.owner, request.id)
  );

  const deleteFishGroup = usePost<AxiosResponse, { localSiteId: string; fishGroupId: string }>(
    (request) => deleteVirtualFishGroup(request.localSiteId, request.fishGroupId),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      history.push(`/planning-prognosis/sites/active/${params.localSiteId}/${params.fishGroupTab}`);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // #endregion POST DATA

  // #region HANDLERS

  const handleChangeFishGroup = (value: string) => {
    Object.assign(params, { fishGroupId: value });
    history.push(generatePath(path, params));
  };

  // #endregion HANDLERS

  // #region RENDERS

  const isEditable = (): boolean => {
    return Boolean(!fishGroup?.recalculating) && Boolean(account.company?.features.hasPlanningPrognosis);
  };

  const isVirtual = (unitId: string): boolean => {
    return unitId === '00000000-0000-0000-0000-000000000000';
  };

  const renderFishGroup = () => {
    let rows: FishGroup[] = [];

    switch (params.fishGroupTab) {
      case Tabs.FISH_GROUPS:
        rows = _.chain(fishGroups)
          .filter((fg) => fg.unitId !== '00000000-0000-0000-0000-000000000000')
          .value();
        break;
      case Tabs.VIRTUAL:
        rows = _.chain(fishGroups)
          .filter((fg) => fg.unitId === '00000000-0000-0000-0000-000000000000')
          .value();
        break;
      case Tabs.HARVESTED:
        rows = _.chain(harvested)
          .filter((fg) => fg)
          .value();
        break;

      default:
        break;
    }

    return _.chain(rows)
      .sortBy((fg) => fg.unitName.toLowerCase())
      .value();
  };

  // #endregion RENDERS

  return isError ? (
    <Error error={error} />
  ) : !site || !fishGroup || !defaultProfile || isLoading ? (
    <LoadingSpinner />
  ) : (
    <>
      <PlanningPrognosisTabsHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: site.siteName,
                url: `${site.siteId}/fishgroups`,
              },
              {
                text: fishGroup?.fishGroupName,
                url: fishGroup?.fishGroupName,
              },
            ]}
          />
        }
      />

      <FilterSidebar>
        <Select
          size="medium"
          margin="none"
          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.profileList.profileLevelFishGroup` })}
          value={fishGroup.fishGroupId}
          onChange={(event) => handleChangeFishGroup(String(event.target.value))}>
          {renderFishGroup().map((fg) => (
            <RadioSelectItem key={fg.fishGroupId} value={fg.fishGroupId} selected={fg.fishGroupId === fishGroup.fishGroupId}>
              {fg.unitName} {fg.fishGroupName}
            </RadioSelectItem>
          ))}
        </Select>
        {isVirtual(fishGroup.unitId) && (
          <Dialog
            maxWidth="sm"
            title={`Delete (${fishGroup.fishGroupName})`}
            ref={removeDialogRef}
            button={
              <Button size="small" type="button" disabled={deleteFishGroup.isPending}>
                <FormattedMessage id="general.delete" />
              </Button>
            }>
            <>
              {deleteFishGroup.isPending && <LoadingSpinner overlay />}
              <Box display={'flex'} flexDirection={'column'} gap={4}>
                <Box>
                  <FormattedMessage id="general.deleteComment" />
                </Box>
                <Box display={'flex'} justifyContent={'end'} gap={4}>
                  <Button variant="outlined" type="submit" size="small" onClick={() => removeDialogRef.current?.close()}>
                    <FormattedMessage id="general.cancel" />
                  </Button>
                  <Button
                    type="button"
                    disabled={deleteFishGroup.isPending}
                    onClick={() => {
                      deleteFishGroup.mutate({
                        localSiteId: fishGroup.localSiteId,
                        fishGroupId: fishGroup.fishGroupId,
                      });
                    }}>
                    <FormattedMessage id="general.delete" />
                  </Button>
                </Box>
              </Box>
            </>
          </Dialog>
        )}
      </FilterSidebar>

      {calculationStatus?.recalculating && (
        <Alert severity="success" icon={<CircularProgress size={18} />} style={{ marginBottom: 50 }}>
          <Mui.AlertTitle>
            <FormattedMessage id="planningPrognosis.alert.title" />
          </Mui.AlertTitle>
          <FormattedMessage id="planningPrognosis.alert.info" />
        </Alert>
      )}

      {!calculationStatus?.recalculating && fishGroup.recalculationNeeded && account.company?.features.hasPlanningPrognosis && (
        <Alert
          severity="warning"
          action={
            <Button
              disabled={startCalculation.isPending || calculationStatus?.recalculating}
              size="small"
              variant="outlined"
              onClick={() => startCalculation.mutate({ owner: OwnerType.FISH_GROUP, id: fishGroup.fishGroupId })}>
              <FormattedMessage id={`general.recalculate`} />
            </Button>
          }
          sx={{ mb: 6, alignItems: 'center' }}>
          <FormattedMessage id={`general.recalculateIsNeeded`} />
        </Alert>
      )}

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {isVirtual(fishGroup.unitId) ? (
            <EditStocking fishGroup={fishGroup} isEditable={isEditable()} />
          ) : (
            <FishGroupStocking fishGroup={fishGroup} isEditable={isEditable()} />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FishGroupPlanning
            defaultProfile={defaultProfile}
            fishGroupId={fishGroup.fishGroupId}
            globalSiteId={site.globalSiteId}
            isEditable={isEditable()}
          />
        </Grid>
        <Grid size={12}>
          <TemporaryPlanning fishGroupId={params.fishGroupId} isEditable={isEditable()} />
        </Grid>
        <Grid size={12}>
          <FishGroupSnapshot
            fishGroup={fishGroup}
            isEditable={isEditable()}
            selectedSnapshot={selectedSnapshot}
            setSelectedSnapshot={setSelectedSnapshot}
          />
        </Grid>
        <Grid size={12}>
          <FishGroupGraph fishGroupId={fishGroup.fishGroupId} selectedSnapshot={selectedSnapshot} />
        </Grid>
      </Grid>
    </>
  );
};

export default ViewFishGroup;
